import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { TicketWorker, TimeEntry, TicketPart } from "@/lib/types"

// Workers
export function useTicketWorkers(ticketId?: string) {
  return useQuery({
    queryKey: ["service_tickets", ticketId, "workers"],
    queryFn: () => api.get<{ data: TicketWorker[] }>(`/api/v1/service_tickets/${ticketId}/workers`),
    enabled: !!ticketId,
  })
}

export function useAddTicketWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, ...data }: { ticketId: string; user_id: string; role?: string; hourly_rate?: number; notes?: string }) =>
      api.post(`/api/v1/service_tickets/${ticketId}/workers`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "workers"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useRemoveTicketWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, userId }: { ticketId: string; userId: string }) =>
      api.delete(`/api/v1/service_tickets/${ticketId}/workers/${userId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "workers"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

// Time Entries
export function useTicketTimeEntries(ticketId?: string) {
  return useQuery({
    queryKey: ["service_tickets", ticketId, "time_entries"],
    queryFn: () => api.get<{ data: TimeEntry[] }>(`/api/v1/service_tickets/${ticketId}/time_entries`),
    enabled: !!ticketId,
  })
}

export function useCreateTimeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, ...data }: { ticketId: string; started_at?: string; ended_at?: string; duration_minutes?: number; description?: string; billable?: boolean }) =>
      api.post<{ data: TimeEntry }>(`/api/v1/service_tickets/${ticketId}/time_entries`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "time_entries"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, entryId, ...data }: { ticketId: string; entryId: string; description?: string; billable?: boolean; started_at?: string; ended_at?: string }) =>
      api.patch<{ data: TimeEntry }>(`/api/v1/service_tickets/${ticketId}/time_entries/${entryId}`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "time_entries"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useStopTimeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, entryId }: { ticketId: string; entryId: string }) =>
      api.post<{ data: TimeEntry }>(`/api/v1/service_tickets/${ticketId}/time_entries/${entryId}/stop`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "time_entries"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, entryId }: { ticketId: string; entryId: string }) =>
      api.delete(`/api/v1/service_tickets/${ticketId}/time_entries/${entryId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "time_entries"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

// Parts
export function useTicketParts(ticketId?: string) {
  return useQuery({
    queryKey: ["service_tickets", ticketId, "parts"],
    queryFn: () => api.get<{ data: TicketPart[] }>(`/api/v1/service_tickets/${ticketId}/parts`),
    enabled: !!ticketId,
  })
}

export function useAddTicketPart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, ...data }: { ticketId: string; name: string; quantity?: number; unit?: string; unit_price?: number; product_id?: string; notes?: string }) =>
      api.post<{ data: TicketPart }>(`/api/v1/service_tickets/${ticketId}/parts`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "parts"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useUpdateTicketPart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, partId, ...data }: { ticketId: string; partId: string; name?: string; quantity?: number; unit_price?: number; notes?: string }) =>
      api.patch<{ data: TicketPart }>(`/api/v1/service_tickets/${ticketId}/parts/${partId}`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "parts"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useDeleteTicketPart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, partId }: { ticketId: string; partId: string }) =>
      api.delete(`/api/v1/service_tickets/${ticketId}/parts/${partId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "parts"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}
